import { Flex, showToast } from '@lokalise/louis';
import { useState } from 'react';

import { type StyleGuide, StyleGuideStates, useSyncStyleGuideWithAIMutation } from '@app/api/styleGuide';
import assertIsNever from '@app/utils/assertIsNever';
import { formatDate } from '@app/utils/i18n';

import { useStyleGuideContext } from '../../StyleGuideContext';
import { AlignedRightCell, CellContainer, RowContainer, SyncButtonContainer } from '../../styles';
import splitFileName from '../../utils/splitFileName';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ProjectsSelector from '../ProjectsSelector/ProjectsSelector';
import StyleGuideInlineValueEditor from '../StyleGuideInlineValueEditor';
import {
    ContentErrorStateButton,
    ContentLengthErrorStateButton,
    GeneralErrorStateButton,
    InProgressStateButton,
    SyncedStateButton,
    TokenLimitExceededWarningStateButton,
    UninitializedStateButton,
} from '../SyncButton';
import ActionMenuButton from './ActionMenuButton';
import { CreatedByContainer, CreatedByDate, CreatedByName, StyleGuideName } from './style';

type StyleGuideRowProps = {
    styleGuide: StyleGuide;
};

export const StyleGuideRow = ({ styleGuide }: StyleGuideRowProps) => {
    const [isFileRenameLoading, setIsFileRenameLoading] = useState(false);
    const [isFileRenameOpen, setIsFileRenameOpen] = useState(false);
    const { onDelete, onOpen, onEdit, saveStyleGuides, teamId } = useStyleGuideContext();

    const { name, language, projects, createdBy, createdAt, summary, id } = styleGuide;
    const { name: nameWithoutExtension, extension } = splitFileName(name);

    const [syncWithAI, { isLoading }] = useSyncStyleGuideWithAIMutation();

    const onSyncClick = async () => {
        await syncWithAI({ teamId, styleGuideId: id });
    };

    const onStyleGuideNameChange = async (stagedValue: string) => {
        setIsFileRenameLoading(true);
        const response = await saveStyleGuides([{ id, name: stagedValue }]);

        if ('error' in response) {
            showToast({ type: 'error', title: 'Something went wrong — please try again' });
        }

        setIsFileRenameLoading(false);
    };

    const syncButtonState = () => {
        switch (summary.status) {
            case StyleGuideStates.uninitialized:
                return <UninitializedStateButton loading={isLoading} onClick={onSyncClick} />;

            case StyleGuideStates.inProgress:
                return <InProgressStateButton />;

            case StyleGuideStates.synced:
                return <SyncedStateButton onClick={() => onEdit(styleGuide)} />;

            case StyleGuideStates.contentError:
                return <ContentErrorStateButton />;

            case StyleGuideStates.tokenLimitExceeded:
                return <TokenLimitExceededWarningStateButton />;

            case StyleGuideStates.promptSizeLimitExceeded:
                return <ContentLengthErrorStateButton />;

            case StyleGuideStates.downloadFailed:
            case StyleGuideStates.languageError:
            case StyleGuideStates.promptError:
            case StyleGuideStates.generalError:
                return <GeneralErrorStateButton loading={isLoading} onClick={onSyncClick} />;

            default:
                return assertIsNever(summary.status);
        }
    };

    return (
        <RowContainer>
            <CellContainer colSpan={isFileRenameOpen ? 2 : 1}>
                <StyleGuideName>
                    <StyleGuideInlineValueEditor
                        required
                        loading={isFileRenameLoading}
                        value={nameWithoutExtension}
                        areaLabel={nameWithoutExtension}
                        onEditStart={() => setIsFileRenameOpen(true)}
                        onEditCanceled={() => setIsFileRenameOpen(false)}
                        onEditSubmitted={() => setIsFileRenameOpen(false)}
                        responseHandler={async (value) => onStyleGuideNameChange(`${value}.${extension}`)}
                    />
                </StyleGuideName>
            </CellContainer>
            {!isFileRenameOpen && (
                <CellContainer>
                    <LanguageSelector styleGuideId={id} assignedLanguage={language} />
                </CellContainer>
            )}
            <CellContainer>
                <ProjectsSelector styleGuideId={id} assignedProjects={projects} />
            </CellContainer>
            <CellContainer>
                <CreatedByContainer>
                    <Flex direction="column">
                        <CreatedByName>{createdBy.name}</CreatedByName>
                        <CreatedByDate>{formatDate(new Date(createdAt), 'PP')}</CreatedByDate>
                    </Flex>
                </CreatedByContainer>
            </CellContainer>
            <CellContainer>
                <SyncButtonContainer>{syncButtonState()}</SyncButtonContainer>
            </CellContainer>
            <CellContainer>
                <AlignedRightCell>
                    <ActionMenuButton
                        withSummary={summary.text !== ''}
                        onDelete={() => onDelete(styleGuide)}
                        onOpen={() => onOpen(id)}
                        onEdit={() => onEdit(styleGuide)}
                    />
                </AlignedRightCell>
            </CellContainer>
        </RowContainer>
    );
};

export default StyleGuideRow;
